import { noop } from 'lodash';
import type { Context, FC, PropsWithChildren } from 'react';
import React, { createContext, useCallback, useMemo, useState } from 'react';

import type {
  ContentContainerContextValue,
  ContentContainerLayoutOptions,
} from './ContentContainerContextProvider.types';
import type { ContentSidebarPosition } from './ContentSidebarColumn.types';

export const ContentContainerContext: Context<ContentContainerContextValue> =
  createContext<ContentContainerContextValue>({
    totalMountedSidebars: 0,
    onSidebarMounted: noop,
    onSidebarUnmounted: noop,
    sidebarPosition: 'right',
    contentContainerWidth: Infinity,
    onContentContainerWidthChanged: noop,
    shouldFillAvailableSpace: false,
    shouldDisableScroll: false,
    shouldShowSubHeaderAtAllBreakpoints: false,
    setContentContainerLayoutOptions: noop,
  });

const ContentContainerContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [totalMountedSidebars, setTotalMountedSidebars] = useState(0);
  const [contentContainerWidth, setContentContainerWidth] = useState<number>(Infinity);
  const [sidebarPosition, setSidebarPosition] = useState<ContentSidebarPosition>('right');
  const [shouldFillAvailableSpace, setShouldFillAvailableSpace] = useState(false);
  const [shouldDisableScroll, setShouldDisableScroll] = useState(false);
  const [shouldShowSubHeaderAtAllBreakpoints, setShouldShowSubHeaderAtAllBreakpoints] = useState(false);

  const onSidebarMounted = useCallback((position: ContentSidebarPosition) => {
    setTotalMountedSidebars((prev) => prev + 1);
    setSidebarPosition(position);
  }, []);

  const onSidebarUnmounted = useCallback(() => {
    setTotalMountedSidebars((prev) => {
      const newTotal = Math.max(0, prev - 1);
      if (newTotal === 0) {
        setSidebarPosition('right');
      }

      return newTotal;
    });
  }, []);

  const onContentContainerWidthChanged = useCallback((newWidth: number) => {
    setContentContainerWidth((prev) => (prev === newWidth ? prev : newWidth));
  }, []);

  const setContentContainerLayoutOptions = useCallback((options: ContentContainerLayoutOptions) => {
    setShouldFillAvailableSpace((prev) =>
      prev === options.shouldFillAvailableSpace ? prev : options.shouldFillAvailableSpace
    );
    setShouldDisableScroll((prev) => (prev === options.shouldDisableScroll ? prev : options.shouldDisableScroll));
    setShouldShowSubHeaderAtAllBreakpoints((prev) =>
      prev === options.shouldShowSubHeaderAtAllBreakpoints ? prev : options.shouldShowSubHeaderAtAllBreakpoints
    );
  }, []);

  const value: ContentContainerContextValue = useMemo(
    () => ({
      totalMountedSidebars,
      onSidebarMounted,
      onSidebarUnmounted,
      sidebarPosition,
      contentContainerWidth,
      onContentContainerWidthChanged,
      shouldFillAvailableSpace,
      shouldDisableScroll,
      shouldShowSubHeaderAtAllBreakpoints,
      setContentContainerLayoutOptions,
    }),
    [
      totalMountedSidebars,
      onSidebarMounted,
      onSidebarUnmounted,
      sidebarPosition,
      contentContainerWidth,
      onContentContainerWidthChanged,
      shouldFillAvailableSpace,
      shouldDisableScroll,
      shouldShowSubHeaderAtAllBreakpoints,
      setContentContainerLayoutOptions,
    ]
  );

  return <ContentContainerContext.Provider value={value}>{children}</ContentContainerContext.Provider>;
};

export default ContentContainerContextProvider;
